import React from "react";
import { Home, Car, Landmark, ShieldCheck, ShieldAlert } from "lucide-react";
import { centsToUSD } from "@/lib/dates";

const StatCell = ({ label, value, mono = true, tone = "text-white" }) => (
  <div>
    <div className="jea-caption">{label}</div>
    <div className={`text-[18px] font-bold ${mono ? "jea-mono" : ""} ${tone}`}>{value}</div>
  </div>
);

/**
 * Renders the consolidated (cross-broker) summary block for a query results page
 * and the Report page.
 */
const ConsolidatedAssetSummary = ({ summary, consolidated }) => {
  if (!summary) return null;
  const addr = summary.current_address;
  const emp = summary.current_employer;
  const bk = summary.bankruptcy_status || "none";
  return (
    <div className="bg-bg-secondary border border-border-default rounded-[12px] p-6 space-y-6"
         data-testid="consolidated-asset-summary">
      <div className="flex items-center justify-between">
        <div>
          <div className="jea-caption">Consolidated Asset Summary</div>
          <h3 className="text-white font-bold text-[18px] mt-1">Cross-broker recap</h3>
        </div>
        <div className="text-right">
          <div className="jea-caption">Total Estimated Assets</div>
          <div className="jea-mono text-accent-pink font-bold text-[24px]" data-testid="consolidated-total-assets">
            {centsToUSD(summary.total_estimated_assets_cents || 0)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <StatCell
          label="Real Property"
          value={centsToUSD(summary.total_property_value_cents || 0)}
          tone="text-white"
        />
        <StatCell
          label="Vehicles"
          value={centsToUSD(summary.total_vehicle_value_cents || 0)}
          tone="text-white"
        />
        <StatCell
          label="Verified Income (Annual)"
          value={summary.verified_income_cents > 0
            ? centsToUSD(summary.verified_income_cents)
            : "—"}
          tone={summary.verified_income_cents > 0 ? "text-success" : "text-text-muted"}
        />
        <StatCell
          label="Bankruptcy Status"
          value={bk.toUpperCase()}
          tone={bk === "active" ? "text-danger" : bk === "discharged" ? "text-warning" : "text-text-secondary"}
          mono={false}
        />
      </div>

      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border-default">
        <div className="space-y-1">
          <div className="jea-caption">Current Address</div>
          <div className="text-white text-[14px]">
            {addr ? (
              <>{addr.street}, {addr.city}, {addr.state} {addr.zip}</>
            ) : "—"}
          </div>
        </div>
        <div className="space-y-1">
          <div className="jea-caption">Current Employer</div>
          <div className="text-white text-[14px]">
            {emp?.name ? (
              <>
                <span className="font-semibold">{emp.name}</span>
                <span className="text-text-secondary"> · {emp.position || "—"}</span>
              </>
            ) : "—"}
          </div>
        </div>
      </div>

      {/* Bank accounts + business associations if present */}
      {(consolidated?.business_associations?.length || 0) > 0 && (
        <div className="pt-4 border-t border-border-default">
          <div className="jea-caption mb-2">Business Associations</div>
          <ul className="space-y-1 text-[13px]">
            {consolidated.business_associations.slice(0, 4).map((b, i) => (
              <li key={i} className="flex items-center gap-2 text-text-secondary">
                <Landmark size={12} className="text-text-muted" />
                <span className="text-white">{b.entity_name}</span>
                <span>·</span>
                <span>{b.role}</span>
                <span>·</span>
                <span className="capitalize">{b.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ConsolidatedAssetSummary;
